import { offlineQueue, QueueItem, QueueEntity } from './offline-queue.service';
import { authService } from './auth.service';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

type EndpointResolver = (item: QueueItem) => {
  url: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: any;
};

const resolvers: Record<QueueEntity, EndpointResolver> = {
  advance: (item) => {
    if (item.action === 'create') {
      const p = item.payload as any;
      return {
        url: `${API_URL}/api/advances`,
        method: 'POST',
        body: {
          project_id: p.project_id,
          week_number: p.week_number,
          year: p.year,
          week_start: p.week_start,
          week_end: p.week_end,
          description: p.description,
          progress_percentage: p.progress_percentage,
          photos: p.photos ?? [],
        },
      };
    }
    if (item.action === 'update') {
      const p = item.payload as any;
      return {
        url: `${API_URL}/api/advances/${p.server_id}`,
        method: 'PUT',
        body: {
          description: p.description,
          progress_percentage: p.progress_percentage,
          status: p.status,
        },
      };
    }
    return {
      url: `${API_URL}/api/advances/${(item.payload as any).server_id}`,
      method: 'DELETE',
    };
  },

  message: (item) => {
    const p = item.payload as any;
    if (item.action === 'create') {
      const endpoint = p.channel === 'internal'
        ? `${API_URL}/api/internal/messages`
        : `${API_URL}/api/messages`;
      return {
        url: endpoint,
        method: 'POST',
        body: {
          projectId: p.projectId,
          content: p.content,
          attachments: p.attachments,
        },
      };
    }
    return {
      url: `${API_URL}/api/internal/messages/${p.server_id}`,
      method: 'DELETE',
    };
  },

  'change-order': (item) => {
    const p = item.payload as any;
    if (item.action === 'create') {
      return {
        url: `${API_URL}/api/change-management`,
        method: 'POST',
        body: {
          projectId: p.projectId,
          title: p.title,
          description: p.description,
          category: p.category,
          priority: p.priority,
          items: p.items,
        },
      };
    }
    return {
      url: `${API_URL}/api/change-management/${p.server_id}`,
      method: 'PUT',
      body: { reason: p.reason },
    };
  },

  document: (item) => {
    const p = item.payload as any;
    if (item.action === 'create') {
      return {
        url: `${API_URL}/api/documents`,
        method: 'POST',
        body: {
          project_id: p.project_id,
          name: p.name,
          type: p.type,
          category: p.category,
          url: p.url,
          size: p.size,
          r2_key: p.r2_key,
        },
      };
    }
    return {
      url: `${API_URL}/api/documents/${p.server_id}`,
      method: 'DELETE',
    };
  },
};

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
}

function isTransientError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  if (msg.includes('failed to fetch') || msg.includes('network')) return true;
  if (msg.includes('timeout')) return true;
  if (msg.includes('429')) return true;
  if (msg.includes('500') || msg.includes('502') || msg.includes('503') || msg.includes('504')) return true;
  return false;
}

let isRunning = false;

export const syncEngine = {
  isRunning(): boolean {
    return isRunning;
  },

  async processQueue(): Promise<SyncResult> {
    if (isRunning) {
      return { success: false, synced: 0, failed: 0, errors: ['Sincronización ya en progreso'] };
    }
    if (!navigator.onLine) {
      return { success: false, synced: 0, failed: 0, errors: ['Sin conexión'] };
    }
    if (!authService.getToken()) {
      return { success: false, synced: 0, failed: 0, errors: ['Sesión expirada'] };
    }

    isRunning = true;
    const result: SyncResult = { success: true, synced: 0, failed: 0, errors: [] };

    try {
      const pending = await offlineQueue.listPending();

      for (const item of pending) {
        try {
          await offlineQueue.markSyncing(item.id);
          const resolver = resolvers[item.entity];
          if (!resolver) {
            throw new Error(`Entidad '${item.entity}' no soportada`);
          }
          const { url, method, body } = resolver(item);

          const response = await authService.fetchWithAuth(url, {
            method,
            body: body ? JSON.stringify(body) : undefined,
          });

          await offlineQueue.markSynced(item.id);
          result.synced += 1;

          const syncedItem = await offlineQueue.get(item.id);
          if (syncedItem) {
            window.dispatchEvent(
              new CustomEvent('offline-synced', {
                detail: {
                  entity: item.entity,
                  localRef: item.localRef,
                  serverData: response?.data ?? null,
                },
              })
            );
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Error desconocido';
          result.errors.push(`${item.entity}:${item.localRef} → ${message}`);
          await offlineQueue.markFailed(item.id, message);
          result.failed += 1;
          if (!isTransientError(error)) {
            continue;
          }
        }
      }

      await offlineQueue.clearSynced();
      result.success = result.failed === 0;
    } catch (error) {
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Error general');
    } finally {
      isRunning = false;
    }

    return result;
  },

  async flushFailed(): Promise<void> {
    const failed = await offlineQueue.listByStatus('failed');
    for (const item of failed) {
      await offlineQueue.updateRetry(item.id);
    }
  },
};